import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import api, { APIError, ApiErrorCodes } from 'helpers/api'
import { IContextMenuState, ILogObject, OptionProps, IProjectMember, IProject, IUserObject } from 'types'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useAuth } from 'hooks/auth'
import DataTable, { dataTableColumns } from 'components/data-table'
import ItemCell, { ItemAvatar } from 'components/data-table/itemCell'
import { Code, Copy, Globe, Process, Spreadsheet, Sync } from 'components/icons'
import Button from 'components/button'
import Input from 'components/input'
import Tooltip from 'components/tooltip'
import Subscription from 'components/subscription'
import HubBadge from 'components/sidebar/hubBadge'
import Popover, { popoverPlacement } from 'components/popover'
import DateRange from 'components/date-range'
import EventFilter from 'components/audit/eventFilter'
import FilterMenu from 'components/filter-menu'
import { parseChange, prettyJson } from 'components/audit/change'
import { SETTINGS_CANVAS, SettingsCard, TextLink } from 'views/settings/components/settingsCard'
import { dateTime } from 'helpers/date'
import {
  AUDIT_RED_KEYWORDS,
  AUDIT_GREEN_KEYWORDS,
  PROJECT,
  DEFAULT_AVATAR_PATH,
  AUDIT_LOGS_BLUR_DAYS,
  ORG_PLAN_TIERS
} from 'app-constants'
import { capitaliseFirstLetter, validateSourceUrl } from 'helpers/utils'
import { HUB_BASE, DOCUMENT_BASE } from 'routes'

interface AuditProps {
  /** What the log is for: 'project' (a hub), 'table' or 'process' (a document). Not needed on the admin route. */
  type?: string
  resourceId?: string
  /** Text the log is narrowed to, such as a row's id for a row's audit trail. */
  searchTerm?: string
  /** Event types chosen when the log opens. */
  verbs?: string[]
  includeChange?: boolean
  className?: string
  /**
   * 'settings' sets the log in a card on a hub settings page, its filters across the card's top and
   * "Get More Logs" in its footer. 'panel' (the default) fills a modal or admin page.
   */
  variant?: 'panel' | 'settings'
}

/** A log as the table lists it: its place in the list as its id, as two logs can share a publicId. */
interface AuditRow {
  id: string
  log: ILogObject
}

interface AuditConfig {
  search: string
  page: number
  users: string[]
  email: string
  verbs: string[]
  projects: string[]
  startDate: string
  endDate: string
}

// The filters that choose from a list, any of which can have nothing chosen.
type ListFilter = 'users' | 'verbs' | 'projects'
const LIST_FILTERS: ListFilter[] = ['users', 'verbs', 'projects']
const filterNoun = (filter: ListFilter) => ({ users: 'users', verbs: 'event types', projects: `${PROJECT}s` })[filter]

// The API sends logs 50 at a time; fewer than that means there are no more.
const PAGE_SIZE = 50
const DAY_MS = 1000 * 60 * 60 * 24
const LINK = 'text-body hover:underline! underline-offset-2'
// The sidebar's icon for a document or table, as on Archived files: 14px, a little heavier, a little lighter.
const RESOURCE_ICON = 'flex shrink-0 opacity-70 [&>svg]:size-3.5 [&_path[stroke]]:[stroke-width:2.4]'
// A log older than the plan keeps: still there, but blurred and out of reach.
const BLURRED_ROW = 'pointer-events-none select-none cursor-default [&>td]:blur-[7px]'

/** The event types a log can be filtered by: only those that can happen to what it's the log of. */
const eventTypesFor = (type: string | undefined, eventTypes: string[]): string[] => {
  if (type === 'process')
    return eventTypes.filter(
      (eventType) =>
        eventType.startsWith('document') ||
        eventType.startsWith('table.cell') ||
        eventType.startsWith('table.row') ||
        eventType.startsWith('table.sync_completed') ||
        eventType.startsWith('table.sync_error') ||
        eventType.startsWith('table.script_error') ||
        eventType.startsWith('table.join') ||
        eventType.includes('comment') ||
        eventType.includes('thread') ||
        eventType.includes('csv') ||
        eventType.includes('policy')
    )
  if (type === 'table')
    return eventTypes.filter(
      (eventType) =>
        !(eventType.startsWith('hub') || eventType.startsWith('member') || eventType.startsWith('document'))
    )
  return eventTypes
}

/** A dropdown option as a filter menu's: every option here has a value. */
const toFilterOption = (option: OptionProps) => ({ value: option.value ?? '', label: option.label })

const byLabel = (a: OptionProps, b: OptionProps) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())

/**
 * Whether a log is blurred: older than the user's plan keeps logs for, or any log on the free plan.
 * Super admins see every log.
 */
export const isBlurred = (log: ILogObject, user: IUserObject | undefined, onPaidPlan: boolean): boolean => {
  if (user?.isSuperAdmin) return false
  if (!onPaidPlan) return true
  if (!user) return false
  const maxDays = AUDIT_LOGS_BLUR_DAYS[user.team?.orgPlanTier || ORG_PLAN_TIERS.PROFESSIONAL]
  return (Date.now() - Date.parse(log.createdAt)) / DAY_MS > maxDays
}

/** Which upgrade the user is told about: the Team plan for logs older than 30 days, or a paid plan for any. */
const upsellFor = (user: IUserObject | undefined, onPaidPlan: boolean): 'team' | 'paid' | null => {
  if (!user || user.isSuperAdmin) return null
  if (!onPaidPlan) return 'paid'
  if ((user.team?.orgPlanTier || ORG_PLAN_TIERS.PROFESSIONAL) === ORG_PLAN_TIERS.PROFESSIONAL) return 'team'
  return null
}

/** Shown, blurred, in place of the logs when the plan doesn't include them. */
const placeholderLogs = (message: string): ILogObject[] =>
  Array.from({ length: 10 }, (_, index) => ({
    publicId: `placeholder-${index}`,
    user: {
      firebaseUserId: 'YYYY',
      email: 'AAAA',
      profilePicture: DEFAULT_AVATAR_PATH,
      name: 'System',
      kind: 'User'
    },
    contextProject: null,
    contextProcess: null,
    contextProcessSection: null,
    contextProcessResponse: null,
    contextTable: null,
    contextTableView: null,
    contextTableColumn: null,
    verb: 'created',
    resource: 'table',
    change: message,
    createdAt: new Date().toISOString(),
    context: null
  }))

/** The date range's ends as the API takes them; the end date is inclusive, so it runs to the next day. */
const dateRange = (config: AuditConfig) => {
  const startDate =
    config.startDate === '' || isNaN(Date.parse(config.startDate)) ? null : new Date(config.startDate).toISOString()
  const endDate =
    config.endDate === '' || isNaN(Date.parse(config.endDate))
      ? null
      : new Date(new Date(config.endDate).getTime() + DAY_MS).toISOString()
  return { startDate, endDate }
}

/** When it happened: the day, and the time to the second. The API's times are UTC without saying so, hence dateTime. */
const logTime = (createdAt: string) => {
  const when = dateTime(new Date(Date.parse(createdAt)))
  return {
    day: when.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
    time: when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }
}

/** The day, with the time under it in secondary text. */
const DateCell: React.FC<{ createdAt: string }> = ({ createdAt }) => {
  const { day, time } = logTime(createdAt)
  return (
    <span className="flex flex-col gap-0.5 tabular-nums">
      <span className="leading-[18px]">{day}</span>
      <span className="text-[12px] leading-4 text-muted">{time}</span>
    </span>
  )
}

const actionTone = (verb: string) => {
  if (AUDIT_RED_KEYWORDS.some((keyword) => verb.includes(keyword))) return 'bg-red/15'
  if (AUDIT_GREEN_KEYWORDS.some((keyword) => verb.includes(keyword))) return 'bg-green/20'
  return 'bg-blue/15'
}

const documentHref = (log: ILogObject) => {
  if (!log.contextProject || !log.contextProcess) return null
  const href = `${HUB_BASE}${log.contextProject.publicId}${DOCUMENT_BASE}${log.contextProcess.publicId}`
  return log.contextProcessSection?.publicId ? `${href}?section=${log.contextProcessSection.publicId}` : href
}

const viewHref = (log: ILogObject) =>
  log.contextTableView?.publicId
    ? `${HUB_BASE}${log.contextProject?.publicId}/table/${log.contextTableView.tablePublicId}/view/${log.contextTableView.publicId}`
    : null

/** The table a log is about, opened at its view, or at the view alone when that's all the log has. */
const tableLink = (log: ILogObject) => {
  const table = log.contextTable
  if (table?.publicId) {
    const viewId = log.contextTableView?.publicId || table.defaultViewId
    return {
      href: `${HUB_BASE}${log.contextProject?.publicId}/table/${table.publicId}/view/${viewId}`,
      name: table.name
    }
  }
  const href = viewHref(log)
  return href ? { href, name: log.contextTableView?.name } : null
}

/** A link that opens in a new tab, in the body colour. */
const NewTabLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a className={LINK} href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

/** Who did it: their picture, their name (opening their profile) and their email under it. */
const UserCell: React.FC<{ log: ILogObject }> = ({ log }) => {
  if (!log.user?.name) return <ItemCell media={<ItemAvatar src={validateSourceUrl(undefined)} />} title="Anonymous" />
  const title = log.user.firebaseUserId ? (
    <a
      className={`font-medium! ${LINK}`}
      href={`/profile/${log.user.firebaseUserId}/information`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {log.user.name}
    </a>
  ) : (
    log.user.name
  )
  return (
    <ItemCell
      media={<ItemAvatar src={validateSourceUrl(log.user.profilePicture)} />}
      title={title}
      description={log.user.email}
    />
  )
}

// How an action reached Morta: through the app, through the API with an API key, or by Morta itself
// with no request behind it (a sync, a script, a scheduled task).
const CHANNELS: Record<string, { label: string; Icon: React.FC<{ className?: string }> }> = {
  web: { label: 'Web app', Icon: Globe },
  api: { label: 'API', Icon: Code }
}
const AUTOMATIC = { label: 'Automatic (sync, script or scheduled task)', Icon: Sync }

/** The channel as an icon, named on hover. */
const ChannelCell: React.FC<{ channel?: string | null }> = ({ channel }) => {
  const { label, Icon } = (channel && CHANNELS[channel]) || AUTOMATIC
  return (
    <Tooltip content={label}>
      <span className="flex w-fit" role="img" aria-label={label}>
        <Icon />
      </span>
    </Tooltip>
  )
}

/** A part of a resource's line: its name, opening it when there's somewhere to go, the whole of it on hover. */
const Crumb: React.FC<{ text?: string; href?: string | null }> = ({ text, href }) => (
  <Tooltip content={text ?? ''} showOnlyIfTruncated>
    {href ? (
      <a className={`min-w-0 truncate font-medium! ${LINK}`} href={href} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    ) : (
      <span className="min-w-0 truncate font-medium">{text}</span>
    )}
  </Tooltip>
)

const Slash: React.FC = () => (
  <span className="shrink-0 text-muted" aria-hidden>
    /
  </span>
)

/**
 * The hub a log is in, as its badge (opening the hub), named on hover; with `alone`, for an action on the hub
 * itself, followed by its name rather than a slash.
 */
const HubCrumb: React.FC<{ log: ILogObject; alone?: boolean }> = ({ log, alone = false }) => {
  const { project } = useProject()
  const location = useLocation()
  const hub = log.contextProject
  // The admin route has a Hub column of its own.
  if (!hub || location.pathname.includes('/admin/')) return null
  // The hub's colours are known for the hub on show (settings and its documents' and tables' logs).
  const own = hub.publicId === project.publicId
  return (
    <>
      <Tooltip content={hub.name}>
        <a
          className="flex shrink-0"
          href={`${HUB_BASE}${hub.publicId}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={hub.name}
        >
          <HubBadge
            name={hub.name}
            colour={own ? project.primaryColour : undefined}
            textColour={own ? project.fontColour : undefined}
            size={18}
          />
        </a>
      </Tooltip>
      {alone ? <Crumb text={hub.name} href={`${HUB_BASE}${hub.publicId}`} /> : <Slash />}
    </>
  )
}

/**
 * One thing an action was on, on one line: the hub's badge, the sidebar's icon for its kind, its name
 * (opening it), and the part of it the action was on after a slash, opening that part where it can:
 * "Riverside / Plant hire log / All plant".
 */
const ResourceEntry: React.FC<{
  log: ILogObject
  kind: 'document' | 'table'
  name?: string
  href?: string | null
  part?: string
  partHref?: string | null
}> = ({ log, kind, name, href, part, partHref }) => (
  <span className="flex items-center gap-1.5 py-1">
    <HubCrumb log={log} />
    <span className={RESOURCE_ICON} role="img" aria-label={kind === 'document' ? 'Document' : 'Table'}>
      {kind === 'document' ? <Process /> : <Spreadsheet />}
    </span>
    <Crumb text={name} href={href} />
    {part && (
      <>
        <Slash />
        <Crumb text={part} href={partHref} />
      </>
    )}
  </span>
)

/**
 * What the action was on, in one column: the document with its section, and the table with the column (or
 * else the view). Most actions are on one or the other; one on a table in a document has both.
 */
const ResourceCell: React.FC<{ log: ILogObject }> = ({ log }) => {
  const table = log.contextTable
  const view = log.contextTableView
  const hasTable = !!(table || view)
  // An action on the hub itself (a setting, a member): the hub, as its badge and name.
  if (!log.contextProcess && !hasTable)
    return log.contextProject ? (
      <span className="flex items-center gap-1.5 py-1">
        <HubCrumb log={log} alone />
      </span>
    ) : null
  const hub = log.contextProject?.publicId
  // The table's name opens it at its default view; the part after it opens the view the action was in.
  const tableHref = table?.publicId ? `${HUB_BASE}${hub}/table/${table.publicId}/view/${table.defaultViewId}` : null
  const tablePart = log.contextTableColumn?.name ?? (table ? view?.name : undefined)
  const documentAlone =
    log.contextProject && log.contextProcess
      ? `${HUB_BASE}${log.contextProject.publicId}${DOCUMENT_BASE}${log.contextProcess.publicId}`
      : null
  return (
    <span className="flex flex-col">
      {log.contextProcess && (
        <ResourceEntry
          log={log}
          kind="document"
          name={log.contextProcess.name}
          href={documentAlone}
          part={log.contextProcessSection?.name}
          partHref={documentHref(log)}
        />
      )}
      {hasTable && (
        <ResourceEntry
          log={log}
          kind="table"
          name={table?.name ?? view?.name}
          href={tableHref ?? tableLink(log)?.href}
          part={tablePart}
          partHref={viewHref(log)}
        />
      )}
    </span>
  )
}

const ActionCell: React.FC<{ log: ILogObject }> = ({ log }) => (
  <span className={`inline-flex rounded px-1.5 py-0.5 text-[12px] font-medium ${actionTone(log.verb)}`}>
    {log.resource}.{log.verb}
  </span>
)

const CHANGE_WIDTH = 420

/**
 * What an action changed: how many changes, underlined, opening the change as stored (indented JSON) with
 * a quiet copy button. The stored JSON is what webhooks, the API and backups use; this only shows it.
 */
const ChangeCell: React.FC<{ change?: string }> = ({ change }) => {
  const { setSnackbarMessage } = useApplicationStore()
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const entries = parseChange(change)
  if (!entries || !change) return null

  const copy = () => {
    // Started inside a promise, so a browser without the clipboard (which throws straight away) is caught too.
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(change))
      .then(() => setSnackbarMessage({ status: 'success', message: 'Change copied as JSON' }))
      .catch(() => setSnackbarMessage({ status: 'error', message: "The change couldn't be copied" }))
  }

  return (
    <>
      <Button
        internalType="transparent"
        aria-haspopup="dialog"
        aria-expanded={!!at}
        className="h-auto p-0 font-normal text-body underline underline-offset-2 decoration-body/40 hover:bg-transparent hover:text-body hover:decoration-body"
        onMouseDown={(event: React.MouseEvent) => {
          if (at) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAt(
            at
              ? null
              : popoverPlacement(event.currentTarget.getBoundingClientRect(), { width: CHANGE_WIDTH, height: 320 })
          )
        }
      >
        {entries.length === 1 ? '1 change' : `${entries.length} changes`}
      </Button>
      <Popover
        id="audit-change"
        at={at}
        onClose={() => setAt(null)}
        label="Change"
        title={entries.length === 1 ? '1 change' : `${entries.length} changes`}
        width={CHANGE_WIDTH}
      >
        <div className="flex flex-col gap-1.5">
          <pre className="max-h-[280px] overflow-auto whitespace-pre rounded-md border border-divider bg-surface-sunken p-2.5 font-mono text-[12px] leading-5 text-body">
            {prettyJson(change)}
          </pre>
          <div className="flex items-center justify-between">
            <span className="text-[12px] italic text-muted">changes.json</span>
            <Tooltip content="Copy JSON">
              <Button internalType="transparent" aria-label="Copy JSON" className="size-6 p-0" onClick={copy}>
                <Copy className="size-3.5" />
              </Button>
            </Tooltip>
          </div>
        </div>
      </Popover>
    </>
  )
}

// Set once, here: a cell is a component, so columns made afresh on each render would remount every cell.
// Sorting is off throughout: the API sends the logs newest first, a page at a time.
const col = dataTableColumns<AuditRow>()
const COLUMNS = col.columns([
  col.display({
    id: 'name',
    header: 'User',
    enableSorting: false,
    cell: ({ row }) => <UserCell log={row.original.log} />
  }),
  col.display({
    id: 'action',
    header: 'Action',
    enableSorting: false,
    cell: ({ row }) => <ActionCell log={row.original.log} />
  }),
  col.display({
    id: 'createdAt',
    header: 'Date',
    enableSorting: false,
    cell: ({ row }) => <DateCell createdAt={row.original.log.createdAt} />
  }),
  col.display({
    id: 'channel',
    header: 'Channel',
    enableSorting: false,
    meta: { className: 'w-0' },
    cell: ({ row }) => <ChannelCell channel={row.original.log.channel} />
  }),
  col.display({
    id: 'resource',
    header: 'Resource',
    enableSorting: false,
    // As wide as what it names, in full: the rest of the row's width when there's more.
    meta: { className: 'w-full' },

    cell: ({ row }) => <ResourceCell log={row.original.log} />
  }),
  col.display({
    id: 'project',
    header: () => capitaliseFirstLetter(PROJECT),
    enableSorting: false,
    cell: ({ row }) => {
      const hub = row.original.log.contextProject
      return hub ? <NewTabLink href={`${HUB_BASE}${hub.publicId}`}>{hub.name}</NewTabLink> : null
    }
  }),
  col.display({
    id: 'change',
    header: 'Change',
    enableSorting: false,
    meta: { className: 'w-0' },
    cell: ({ row }) => <ChangeCell change={row.original.log.change} />
  })
])

/** What a log is a log of, for its empty state. */
const SUBJECT: Record<string, string> = { project: PROJECT, table: 'table', process: 'document' }

/**
 * The audit log of a hub, table or document, or, on the admin route, of every hub: newest first, 50 at a
 * time, filtered on the server by who did it, the event type, the date and (on the admin route) the hub
 * and the user's email. Logs older than the plan keeps are blurred, with a note on the plan that keeps them.
 */
const Audit: React.FC<AuditProps> = ({
  type,
  resourceId,
  searchTerm,
  verbs,
  includeChange = true,
  className,
  variant = 'panel'
}: AuditProps) => {
  const location = useLocation()
  const isAdminRoute = location.pathname.includes('/admin/')

  const emailTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Which load is the latest, so only it fills the list: an earlier one still on its way is dropped.
  const latestLoad = useRef(0)
  // The resource the logs on show are for: another one starts again from its first page.
  const loadedFor = useRef(resourceId)
  const [loading, setLoading] = useState<boolean>(false)
  // Filters with everything unticked: the log lists nothing, rather than everything as an empty filter would.
  const [none, setNone] = useState<Partial<Record<ListFilter, boolean>>>({})
  const [config, setConfig] = useState<AuditConfig>({
    search: searchTerm ? searchTerm : '',
    page: 1,
    email: '',
    users: [],
    projects: [],
    verbs: verbs ? verbs : [],
    startDate: '',
    endDate: ''
  })

  // Undefined while the first page of logs is loading.
  const [logs, setLogs] = useState<ILogObject[]>()
  const [loadFailed, setLoadFailed] = useState(false)
  const [availableEventNames, setAvailableEventNames] = useState<string[]>([])
  const [loadingEventNames, setLoadingEventNames] = useState<boolean>(true)
  const [users, setUsers] = useState<OptionProps[]>([])
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
  const [projects, setProjects] = useState<OptionProps[]>([])
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false)
  const [noMoreLogs, setNoMoreLogs] = useState<boolean>(false)

  const { user, onPaidPlan } = useAuth()
  const { displayErrorMessage } = useApplicationStore()
  const { project, projectOwner } = useProject()

  useEffect(() => {
    api
      .getNotificationEventTypes()
      .then((response) => setAvailableEventNames(eventTypesFor(type, response.data)))
      .catch((error) => displayErrorMessage(error))
      .then(() => setLoadingEventNames(false))

    if (isAdminRoute) {
      setLoadingProjects(true)
      const projectsCall = resourceId && type ? api.getProjects() : api.getAllProjects(true)
      projectsCall
        .then((response) =>
          setProjects(
            response.data
              .map((hub: IProject) => ({ label: `${hub.name} (${hub.publicId.split('-')[0]})`, value: hub.publicId }))
              .sort(byLabel)
          )
        )
        .catch((error) => {
          displayErrorMessage(error)
          setProjects([])
        })
        .then(() => setLoadingProjects(false))
    }
  }, [])

  // The people of the hub on show, to filter by. Another hub (the settings page stays open across a switch)
  // lists its own, and drops a user filter chosen in the last one.
  const usersFor = useRef(project.publicId)
  useEffect(() => {
    if (isAdminRoute || !resourceId || !type || project.loading || !projectOwner) return
    if (usersFor.current !== project.publicId) {
      usersFor.current = project.publicId
      setConfig((current) => (current.users.length > 0 ? { ...current, users: [], page: 1 } : current))
      setNone((current) => ({ ...current, users: false }))
    }
    let cancelled = false
    setLoadingUsers(true)
    api
      .getProjectMembers(project.publicId)
      .then((response) => {
        if (cancelled) return
        setUsers(
          response.data
            .map((member: IProjectMember) => ({
              label: `${member.user.name} (${member.user.email})`,
              value: member.user.publicId
            }))
            .sort(byLabel)
        )
      })
      .catch((error) => {
        if (cancelled) return
        displayErrorMessage(error)
        setUsers([])
      })
      .then(() => {
        if (!cancelled) setLoadingUsers(false)
      })
    return () => {
      cancelled = true
    }
  }, [project.publicId, project.loading])

  useEffect(() => {
    latestLoad.current += 1
    const thisLoad = latestLoad.current
    // Another resource while later pages are on show (the same window, opened on another table): its
    // logs start from the first page, rather than its page N being added to the last one's.
    if (loadedFor.current !== resourceId) {
      loadedFor.current = resourceId
      if (config.page !== 1) {
        setConfig((current) => ({ ...current, page: 1 }))
        return
      }
    }
    const firstPage = config.page === 1
    const { startDate, endDate } = dateRange(config)
    const search = config.search === '' ? null : config.search

    let request
    if (isAdminRoute)
      request = api.getAdminEvents(
        config.page,
        config.users,
        config.email,
        config.verbs,
        startDate,
        endDate,
        search,
        config.projects
      )
    else if (resourceId && type)
      request = api.getEvents(resourceId, type, config.page, config.users, config.verbs, startDate, endDate, search)
    else {
      // Nothing to load the log of yet.
      setLogs([])
      setNoMoreLogs(true)
      return
    }

    setLoading(true)
    setLoadFailed(false)
    if (firstPage) setLogs(undefined)
    request
      .then((response) => {
        if (thisLoad !== latestLoad.current) return
        const additionalLogs: ILogObject[] = response.data
        setLogs((current) => (firstPage || !current ? additionalLogs : current.concat(additionalLogs)))
        setNoMoreLogs(additionalLogs.length < PAGE_SIZE)
        setLoading(false)
      })
      .catch((error) => {
        if (thisLoad !== latestLoad.current) return
        displayErrorMessage(error)
        if (error instanceof APIError && error.code === ApiErrorCodes.SUBSCRIPTION_LEVEL)
          setLogs(placeholderLogs(error.message))
        else if (firstPage) setLoadFailed(true)
        setLoading(false)
        setNoMoreLogs(true)
      })
  }, [config, resourceId])

  const filtered = (changes: Partial<AuditConfig>) => setConfig((current) => ({ ...current, ...changes, page: 1 }))

  const handleEmailChange = (email: string) => {
    if (emailTimeout.current !== null) clearTimeout(emailTimeout.current)
    emailTimeout.current = setTimeout(() => filtered({ email }), 1000)
  }

  /** Filters by these values: null for none at all, which lists nothing. */
  const choose = (filter: ListFilter, values: string[] | null) => {
    setNone((current) => ({ ...current, [filter]: values === null }))
    if (values !== null) filtered({ [filter]: values })
  }
  // A filter with nothing chosen: nothing to list, and nothing to ask the API for.
  const noneChosen = LIST_FILTERS.find((filter) => none[filter])
  const rows = noneChosen ? [] : logs?.map((log, index): AuditRow => ({ id: String(index), log }))
  const hiddenColumns = [...(isAdminRoute ? [] : ['project']), ...(includeChange ? [] : ['change'])]
  const anyFilter =
    config.users.length > 0 ||
    config.verbs.length > 0 ||
    config.projects.length > 0 ||
    config.email !== '' ||
    config.startDate !== '' ||
    config.endDate !== ''
  const subject = type ? SUBJECT[type] : undefined
  const upsell = upsellFor(user, onPaidPlan)
  const settings = variant === 'settings'

  // At the right; on a page wider than the window, held at its right edge so they stay in reach.
  const filters = (
    <div className={`flex justify-end ${settings ? 'px-4 py-3' : 'mb-3'}`}>
      <div className="sticky right-4 flex flex-wrap items-center justify-end gap-2">
        {isAdminRoute ? (
          <FilterMenu
            noun={{ one: PROJECT, many: `${PROJECT}s` }}
            groups={[{ options: projects.map(toFilterOption) }]}
            selected={none.projects ? null : config.projects}
            onChange={(projects) => choose('projects', projects)}
            loading={loadingProjects}
          />
        ) : (
          <FilterMenu
            noun={{ one: 'user', many: 'users' }}
            groups={[{ options: users.map(toFilterOption) }]}
            selected={none.users ? null : config.users}
            onChange={(users) => choose('users', users)}
            loading={loadingUsers}
          />
        )}
        <EventFilter
          eventTypes={availableEventNames}
          selected={none.verbs ? null : config.verbs}
          onChange={(verbs) => choose('verbs', verbs)}
          loading={loadingEventNames}
        />
        {isAdminRoute && (
          <Input
            type="text"
            aria-label="Filter by user email"
            placeholder="Filter by user email"
            onChange={(event) => handleEmailChange(event.target.value)}
            className="h-8 w-[200px]"
          />
        )}
        <DateRange
          from={config.startDate}
          to={config.endDate}
          onChange={({ from, to }) => filtered({ startDate: from, endDate: to })}
        />
      </div>
    </div>
  )

  const upsellNote = upsell && (
    <Subscription
      type={upsell}
      message={upsell === 'team' ? 'view audit logs older than 30 days' : 'view audit logs'}
    />
  )

  const table = (
    <DataTable
      label="Audit log"
      columns={COLUMNS}
      data={rows}
      getRowId={(row) => row.id}
      hiddenColumns={hiddenColumns}
      paged={false}
      stickyHeader
      rowClassName={(row) => (isBlurred(row.log, user, onPaidPlan) ? BLURRED_ROW : undefined)}
      empty={
        noneChosen ? (
          <>
            <div className="font-medium text-body">No {filterNoun(noneChosen)} are selected</div>
            <div className="mt-1">
              <TextLink onClick={() => choose(noneChosen, [])}>Select all {filterNoun(noneChosen)}</TextLink>
            </div>
          </>
        ) : anyFilter ? (
          'No activity matches the selected filters.'
        ) : (
          <>
            <div className="font-medium text-body">No activity recorded</div>
            {subject && <div className="mt-0.5">Actions taken in this {subject} will be listed here.</div>}
          </>
        )
      }
      error={
        loadFailed && (
          <>
            <div className="font-medium text-body">The audit log couldn&apos;t be loaded</div>
            <div className="mt-1">
              <TextLink onClick={() => setConfig((current) => ({ ...current }))}>Try again</TextLink>
            </div>
          </>
        )
      }
    />
  )

  const loadMore = logs !== undefined && !noMoreLogs && !noneChosen && (
    <Button
      data-tour="audit-get-more-logs"
      internalType="outline"
      onClick={() => setConfig((current) => ({ ...current, page: current.page + 1 }))}
      isLoading={loading}
    >
      Get More Logs
    </Button>
  )

  if (settings)
    return (
      // The log is part of the page, which scrolls as a whole (sideways too, when the log is wider than it):
      // the card is at least as wide as the table, which shows every resource in full, and the table's header
      // sticks to the top of the settings area as the page scrolls. Nothing between them scrolls, or it couldn't.
      <div
        className={`w-full min-w-fit min-h-full ${SETTINGS_CANVAS} [&_section]:min-w-fit [&_[data-slot=table-container]]:overflow-visible`}
        data-testid="audit-settings"
      >
        <SettingsCard
          title="Audit log"
          description={`Actions taken in this ${PROJECT}, newest first, with who took each one and when.`}
          footer={loadMore && <div className="flex w-full justify-center">{loadMore}</div>}
        >
          {filters}
          {upsellNote && <div className="px-4 py-3 [&_[data-testid=subscription-message]]:mb-0">{upsellNote}</div>}
          {table}
        </SettingsCard>
      </div>
    )

  // In a modal or the admin page: the filters, then the logs scrolling under their header. The table's own
  // horizontal scroll is handed to the scrolling area, so the header can stick to its top; and the table
  // reaches 16px past the filters on each side, into the space around the log, so its text lines up with
  // them (nothing here clips, so that overhang shows).
  return (
    <div className={`flex flex-col w-full h-screen ${className || ''}`} data-testid="audit-panel">
      {filters}
      {upsellNote}
      <div className="-mx-4 flex-1 min-h-0 overflow-auto [&_[data-slot=table-container]]:overflow-visible">{table}</div>
      {loadMore && <div className="flex justify-center pt-3">{loadMore}</div>}
    </div>
  )
}

export default Audit
